import {
    TextInput,
    Flex,
    Caption,
    useStyles,
    Button,
    useTheme,
    useAppDisplayModeContext,
    DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV,
    MOBILE_TOP_NAV_HEIGHT_FULL_LOGO,
} from '@archipro-design/aria';
import { Loader, SearchLine } from '@archipro-design/icons';
import type { SyntheticEvent } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import * as S from '@modules/product-tagging/components/product-tagging-search/ProductTaggingSearchModal.style';
import { useFetcher } from '@remix-run/react';
import type { loader } from '~/routes/remix-api.product-tagging.product-search';
import { ProductTaggingProductSearch } from '~/routes/remix-api.product-tagging.product-search';
import { useDebounceFn, useIsomorphicLayoutEffect } from 'ahooks';
import {
    URL_SEARCH_PARAM,
    MIN_PRODUCT_SEARCH_CHARACTERS,
} from '@modules/product-tagging/config/search-form';
import ProductSearchResultTile from '@modules/product-tagging/components/product-search-result-tile/ProductSearchResultTile';
import { Animation } from '@fluentui/react-northstar';
import type { ProductTaggingSearchFormProps } from '@modules/product-tagging/type';
import { useProductTagging } from '@modules/product-tagging/context';

export const ProductSearchForm = ({
    onFormToggle,
    onConfirm,
    popperState,
}: ProductTaggingSearchFormProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const [keyword, setKeyword] = useState('');
    const {
        state: { tagPosition },
        recentlyTaggedProducts,
    } = useProductTagging();
    const ref = useRef<HTMLInputElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const { desktop } = useAppDisplayModeContext();

    const fetcher = useFetcher<typeof loader>();

    useIsomorphicLayoutEffect(() => {
        // checking popperState.rect ensures the popper modal is created and positioned before we can scroll correctly
        if (popperState?.rects && ref.current && modalRef.current) {
            ref.current.focus({ preventScroll: true });
            const rect = modalRef.current.getBoundingClientRect();
            const topOffset = desktop
                ? DESKTOP_TOP_NAV_HEIGHT_WITH_SECOND_NAV
                : MOBILE_TOP_NAV_HEIGHT_FULL_LOGO;
            const isModalInView =
                rect &&
                rect.top >= topOffset &&
                rect.bottom <= window.innerHeight;
            if (!isModalInView) {
                modalRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [popperState, tagPosition, desktop]);

    const { run: searchProduct } = useDebounceFn(
        (event: SyntheticEvent<HTMLElement, Event>) => {
            const target = event.target as HTMLInputElement;
            setKeyword(target.value);
            if (target.value.length >= MIN_PRODUCT_SEARCH_CHARACTERS) {
                fetcher.load(
                    `${ProductTaggingProductSearch}?${URL_SEARCH_PARAM}=${target.value}`
                );
            }
        },
        {
            wait: 300,
            leading: true,
        }
    );

    const initiateSearch = keyword.length >= MIN_PRODUCT_SEARCH_CHARACTERS;
    const productSearchResults = initiateSearch ? fetcher.data || [] : [];
    const loading = fetcher.state === 'loading';

    return (
        <>
            <div className={css(S.SearchField)} ref={modalRef}>
                <TextInput
                    className={css(S.SearchTextInput)}
                    input={{
                        placeholder: 'Search product or supplier',
                        ref,
                    }}
                    size={'md'}
                    suffixIcons={[<SearchLine key={'searchIcon'} />]}
                    onChange={searchProduct}
                />
            </div>
            <Flex column className={css(S.SearchBody)}>
                {productSearchResults.length > 0 ? (
                    productSearchResults.map((tile) => (
                        <ProductSearchResultTile
                            key={tile.pType + tile.id}
                            tileData={tile}
                            showProfessionalName={true}
                            onConfirm={onConfirm}
                        />
                    ))
                ) : (
                    <>
                        {!initiateSearch &&
                        recentlyTaggedProducts.length > 0 ? (
                            <>
                                <Caption
                                    className={css(S.EmptyResults)}
                                    variant="04"
                                    variables={{
                                        captionColor:
                                            theme.siteVariables.colors
                                                .grey[300],
                                    }}
                                >
                                    Recently tagged
                                </Caption>
                                {recentlyTaggedProducts.map((tile) => (
                                    <ProductSearchResultTile
                                        key={tile.pType + tile.id}
                                        tileData={tile}
                                        showProfessionalName={true}
                                        onConfirm={onConfirm}
                                    />
                                ))}
                            </>
                        ) : (
                            <Caption
                                className={css(S.EmptyResults)}
                                variant="04"
                                variables={{
                                    captionColor:
                                        theme.siteVariables.colors.grey[300],
                                }}
                            >
                                No results
                            </Caption>
                        )}
                    </>
                )}
            </Flex>
            <div className={css(S.ModalFooter)}>
                <Button
                    color={'dark'}
                    type={'submit'}
                    size={16}
                    variables={{
                        buttonMinWidth: '100%',
                    }}
                    styles={{
                        textTransform: 'uppercase',
                        color: theme.siteVariables.colors.white['000'],
                    }}
                    onClick={() => onFormToggle('supplier')}
                >
                    {`Can't find product?`}
                </Button>
            </div>
            {loading && (
                <div className={css(S.SearchLoading)}>
                    <Animation name="rotateUltraSlow" className={css(S.Loader)}>
                        <Loader size="2x" />
                    </Animation>
                </div>
            )}
        </>
    );
};
